import { Component, computed, inject, input, output, signal } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../../core/auth/auth.service';
import { ThemeService } from '../../../core/services/theme.service';

const ADMIN_SECTION_LABELS: Record<string, string> = {
  dashboard:  'Dashboard',
  tenants:    'Tenants',
  users:      'Usuários',
  queues:     'Filas',
  schedules:  'Agendamentos',
  platforms:  'Plataformas',
  logs:       'Logs',
};

@Component({
  selector: 'admin-toolbar',
  standalone: true,
  imports: [],
  templateUrl: './admin-toolbar.component.html',
  styleUrl: './admin-toolbar.component.scss',
})
export class AdminToolbarComponent {
  private readonly router = inject(Router);
  readonly authService = inject(AuthService);
  readonly themeService = inject(ThemeService);

  readonly sidebarOpen = input<boolean>(true);
  readonly toggleSidebar = output<void>();

  readonly userMenuOpen = signal(false);
  readonly currentSection = signal<string>(this.resolveSection(this.router.url));

  readonly isDark = computed(() => this.themeService.isDark());
  readonly user = computed(() => this.authService.user());
  readonly userInitials = computed(() => {
    const u = this.authService.user();
    if (!u?.nome) return '';
    const parts = u.nome.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase();
  });

  constructor() {
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd), takeUntilDestroyed())
      .subscribe(e => {
        this.currentSection.set(this.resolveSection((e as NavigationEnd).urlAfterRedirects));
      });
  }

  onToggleSidebar(): void {
    this.toggleSidebar.emit();
  }

  onToggleTheme(): void {
    this.themeService.toggleTheme();
  }

  onToggleUserMenu(): void {
    this.userMenuOpen.update(v => !v);
  }

  onCloseUserMenu(): void {
    this.userMenuOpen.set(false);
  }

  onBackToClient(): void {
    this.userMenuOpen.set(false);
    this.router.navigate(['/selecionar-tenant']);
  }

  onLogout(): void {
    this.userMenuOpen.set(false);
    this.authService.logout();
  }

  private resolveSection(url: string): string {
    const match = url.match(/\/admin\/([^/?#]+)/);
    const segment = match?.[1] ?? 'dashboard';
    return ADMIN_SECTION_LABELS[segment] ?? segment;
  }
}
